import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { uniqBy } from 'lodash';

import 'src/app/popup.scss';
import styles from 'src/app/pages/Administration/tabs/OntologyTab/Ontology.module.scss';

import { OntologyDataType, OntologyResultType } from 'src/types/services/ontology-service.type';
import { reduxRootTypes } from 'src/types';

import { ontologyService } from 'src/app/services';
import { contentsActions } from 'src/app/redux/actions';

import { stringReducer } from 'src/app/helpers/tableFilter';
import { getEnumKeyByValue2 } from 'src/app/helpers/enum.helper';
import { checkPermissionList } from 'src/app/helpers/permissions.helper';
import { displaySuccessStatusMessage } from 'src/app/helpers/status.helper';

import { ontologyEnums } from 'src/enums';
import { PermissionEnum } from 'src/enums/permission.enum';

import OntologyForm from 'src/app/components/Forms/OntologiesForm/ontologiesForm';
import CustomTable from 'src/app/components/CustomTable/CustomTable';

import { deleteConfirmTitles, Item, OntologyPropsType, predefinedOntologies } from './ontology.config';

import useSearch from 'src/app/hooks/useSearch';
import { useTranslation } from 'react-i18next';

import Input from 'antd/es/input';
import Form from 'antd/es/form';
import Popover from 'antd/es/popover';
import Typography from 'antd/es/typography';
import Popconfirm from 'antd/es/popconfirm';
import Tooltip from 'antd/es/tooltip';
import Button from 'antd/es/button/button';
import Checkbox from 'antd/es/checkbox/Checkbox';

const { Search } = Input;

const OntologyTab: React.FC<OntologyPropsType> = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { searchValue, onSearch } = useSearch();

  const [form] = Form.useForm();

  const defaultOntologyData: OntologyDataType = {
    id: '',
    ontologyType: undefined,
    email: undefined,
    phrase: '',
    abbreviation: '',
    definition: '',
  };

  const [addOntologyModal, setAddOntologyModal] = useState<boolean>(false);
  const [modalInEditMode, setModalInEditMode] = useState<boolean>(false);
  const [dataForOntologies, setDataForOntologies] = useState<Item[]>([]);
  const [filteredFormData, setFilteredFormData] = useState<Item[] | null>(null);
  const [ontologiesAllData, setOntologiesAllData] = useState<OntologyResultType[]>([]);
  const [ontologyDataInForm, setOntologyDataInForm] = useState<OntologyDataType>(defaultOntologyData);
  const [currentFilters, setCurrentFilters] = useState({});
  const [isSelectOntologiesActive, setIsSelectOntologiesActive] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  const userState = useSelector((state: reduxRootTypes.RootStateType) => state.userState);
  const { ontologies } = useSelector((state: reduxRootTypes.RootStateType) => state.ontologyState);
  const { eventId } = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);

  const checkIsOntologyDefaultMail = (record: Item) => {
    if (
      record.id === '401c32d4-df6e-4c8e-af1b-d5db0bd4f4a4' &&
      record.abbreviation === 'Mail' &&
      record.phrase === 'Mail'
    ) {
      return true;
    }

    return false;
  };

  const getSortedFilteredData = (data: Item[]) => {
    return data
      .filter((obj) => obj.abbreviation !== 'Root')
      .sort((a, b) =>
        getEnumKeyByValue2(ontologyEnums.OntologyEnum, a.ontologyType)
          .replaceAll('_', '')
          .localeCompare(getEnumKeyByValue2(ontologyEnums.OntologyEnum, b.ontologyType)?.replaceAll('_', '')),
      );
  };

  const tableData = useMemo(() => {
    return getSortedFilteredData(filteredFormData || dataForOntologies);
  }, [filteredFormData, dataForOntologies]);

  const onPageChange = (page: number, pageSize: number) => {
    setPagination({
      current: page,
      pageSize,
    });
  };

  const currentPageData = useMemo(() => {
    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;

    return tableData.slice(start, end);
  }, [filteredFormData, dataForOntologies, pagination]);

  const handleSelectCurrentPage = () => {
    const pageIds = currentPageData.map((x) => {
      if (predefinedOntologies.includes(x.abbreviation)) {
        return;
      }

      return x.id;
    });

    const filteredPageIds = pageIds.filter((id) => id !== undefined);

    setSelectedRowKeys((prev) => Array.from(new Set([...prev, ...(filteredPageIds as string[])])));
  };

  const onClickAddPlayerType = () => {
    if (modalInEditMode) {
      setOntologyDataInForm(defaultOntologyData);
    }
    setAddOntologyModal(true);
    setModalInEditMode(false);
  };

  useEffect(() => {
    const clearData: Item[] = [];

    ontologiesAllData.forEach((item) => {
      clearData.push({
        id: item.id,
        ontologyType: item.ontologyType,
        phrase: item.phrase,
        abbreviation: item.abbreviation,
        definition: item.definition,
      });
    });

    setDataForOntologies(clearData);
  }, [ontologiesAllData]);

  const getOntologiesData = async () => {
    try {
      if (searchValue.length > 0) {
        const compareValue = searchValue.toLowerCase();
        const searchOntologies = ontologies.filter(
          (obj) =>
            obj.abbreviation?.toLowerCase().includes(compareValue) ||
            obj.phrase?.toLowerCase().includes(compareValue) ||
            obj.definition?.toLowerCase().includes(compareValue),
        );
        setOntologiesAllData(searchOntologies);
      } else {
        setOntologiesAllData(ontologies);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getOntologiesData();
  }, [searchValue, ontologies]);

  const onClickShow = (record: Partial<Item> & { id: React.Key }) => {
    setAddOntologyModal(true);
    setModalInEditMode(true);
  };

  const edit = (record: Partial<Item> & { id: React.Key }) => {
    const item = ontologiesAllData.find((o) => o.id === record.id);

    if (item) {
      onClickShow(record);
      setOntologyDataInForm({
        ...item,
      });
    }
  };

  const renderPredefinedOntologies = (value: string) => {
    if (predefinedOntologies.includes(value)) {
      return t(`administration.ontologies.${value}`);
    } else {
      return value;
    }
  };

  const deleteOntology = async (deletedItem: Item) => {
    try {
      await ontologyService.deleteOntologyOfEvent({ id: deletedItem.id.toString() });

      if (deletedItem.id === ontologyDataInForm.id) {
        setOntologyDataInForm(defaultOntologyData);
        // TODO açık olan popup sayfasının kapanması için değişkeni ekle.
      }
      // setOntologiesAllData(ontologiesAllData.filter((item) => item.id !== deletedItem.id));
      // TODO delete işlemi dispach için işlem yapılabilir.
      // dispatch(contentsActions.ontologyList(ontologiesAllData.filter((item) => item.id !== deletedItem.id)));
      dispatch(contentsActions.ontologyDeleted(deletedItem.id));
      displaySuccessStatusMessage(t('success.deleted'));
    } catch (error) {
      console.log(error);
    }
  };

  const deleteOntologiesFromEvent = async (ontologyIds: string[]) => {
    try {
      await ontologyService.deleteOntologiesFromEvent(ontologyIds);

      dispatch(contentsActions.ontologiesDeleted(ontologyIds));
      displaySuccessStatusMessage(t('success.deleted'));
    } catch (error) {
      console.log(error);
    }
  };

  const checkOntologyOperationIsDisabled = (record: Item, operation: 'edit' | 'delete') => {
    if (userState.userName === 'Admin') {
      return false;
    }

    if (
      operation === 'delete'
        ? checkPermissionList(PermissionEnum.OntologiesDelete)
        : checkPermissionList(PermissionEnum.OntologiesUpdate)
    ) {
      return true;
    } else if (checkIsOntologyDefaultMail(record)) {
      return true;
    } else if (predefinedOntologies.includes(record.abbreviation)) {
      return true;
    } else {
      return false;
    }
  };

  const columns = [
    {
      title: t('administration.tabs.ontologies'),
      dataIndex: 'ontologyType',
      width: '15%',
      render: (value: any, record: any) => {
        const convertOntologyTypeofEnum = getEnumKeyByValue2(ontologyEnums.OntologyEnum, record.ontologyType);

        return t(`administration.ontologies.ontologyEnum.${convertOntologyTypeofEnum}`);
      },
      sorter: (a: any, b: any) => {
        return getEnumKeyByValue2(ontologyEnums.OntologyEnum, a.ontologyType).localeCompare(
          getEnumKeyByValue2(ontologyEnums.OntologyEnum, b.ontologyType),
        );
      },
      onFilter: (value: any, record: any) => (record.ontologyType === value) === true,
      filters: stringReducer(
        uniqBy(dataForOntologies, 'ontologyType').map((g) => {
          const convertOntologyTypeofEnum = getEnumKeyByValue2(ontologyEnums.OntologyEnum, g.ontologyType);
          return {
            text: t(`administration.ontologies.ontologyEnum.${convertOntologyTypeofEnum}`),
            value: g.ontologyType,
          };
        }),
      ),
    },
    {
      title: t('administration.ontologies.phrase'),
      dataIndex: 'phrase',
      width: '20%',
      render: (value: string, record: Item) => {
        return renderPredefinedOntologies(value);
      },
    },
    {
      title: t('administration.ontologies.abbreviation'),
      dataIndex: 'abbreviation',
      width: '15%',
      sorter: (a: any, b: any) => {
        if (
          getEnumKeyByValue2(ontologyEnums.OntologyEnum, a.ontologyType) ===
          getEnumKeyByValue2(ontologyEnums.OntologyEnum, b.ontologyType)
        ) {
          return a.abbreviation.localeCompare(b.abbreviation);
        }
        return 0;
      },
      render: (value: string, record: Item) => {
        return renderPredefinedOntologies(value);
      },
    },
    {
      title: t('administration.ontologies.definition'),
      dataIndex: 'definition',
      width: '45%',
      render: (value: string, record: Item) => {
        return renderPredefinedOntologies(value);
      },
    },
    {
      title: t('general.operation.operation'),
      dataIndex: 'operation',
      width: '7%',
      fixed: 'right' as const,
      render: (_: any, record: Item) => {
        return !isSelectOntologiesActive ? (
          <div>
            <Popover content={<div>{t('general.operation.edit')}</div>} trigger="hover">
              <Typography.Link onClick={() => edit(record)} disabled={checkOntologyOperationIsDisabled(record, 'edit')}>
                <img className="edit-icon" src="/assets/editIcon.png" alt="edit" />
              </Typography.Link>
            </Popover>
            <Popover content={<div>{t('general.operation.delete')}</div>} trigger="hover">
              <Popconfirm
                placement="bottom"
                title={deleteConfirmTitles().title}
                okText={deleteConfirmTitles().okText}
                cancelText={deleteConfirmTitles().cancelText}
                icon=""
                onCancel={() => deleteOntology(record)}
                disabled={checkOntologyOperationIsDisabled(record, 'delete')}
              >
                <img
                  className="delete-icon"
                  src="/assets/deleteIcon.png"
                  alt="delete"
                  aria-disabled={checkOntologyOperationIsDisabled(record, 'delete')}
                />
              </Popconfirm>
            </Popover>
          </div>
        ) : (
          <div id={styles.ontologyCheckbox}>
            {checkOntologyOperationIsDisabled(record, 'delete') ? (
              <span></span>
            ) : (
              <Checkbox
                checked={selectedRowKeys.includes(record.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedRowKeys((prev) => [...prev, record.id]);
                  } else {
                    setSelectedRowKeys((prev) => prev.filter((id) => id !== record.id));
                  }
                }}
              />
            )}
          </div>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    return {
      ...col,
      onCell: (record: OntologyResultType) => ({
        record,
        inputType: col.dataIndex === 'type' ? 'number' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
      }),
      title: (
        <Tooltip title={col.title}>
          <span className={styles.tableTitleStyle}>{col.title}</span>
        </Tooltip>
      ),
    };
  });

  useEffect(() => {
    if (dataForOntologies) {
      if (currentFilters && Object.keys(currentFilters).length > 0) {
        handleTableChange(currentFilters);
      } else {
        setFilteredFormData(null);
      }
    }
  }, [dataForOntologies]);

  const handleTableChange = (filters: any) => {
    const { ontologyType } = filters;

    let filteredData = dataForOntologies;

    if (ontologyType && ontologyType.length > 0) {
      filteredData = filteredData.filter((item) => ontologyType.includes(item.ontologyType));
    }

    if (ontologyType && ontologyType.length > 0) {
      setFilteredFormData(filteredData);
      setCurrentFilters(filters);
    } else {
      setFilteredFormData(null);
      setCurrentFilters({});
    }
  };

  return (
    <>
      {checkPermissionList(PermissionEnum.OntologiesRead) === false && eventId !== '' && (
        <div className="database-main-container">
          <div className="database-container">
            <div className="database-add-button-container">
              {!isSelectOntologiesActive ? (
                <>
                  <Button
                    className="database-add-button"
                    onClick={onClickAddPlayerType}
                    disabled={checkPermissionList(PermissionEnum.OntologiesCreate)}
                  >
                    <div className="database-add-button-inner-container">
                      <p className="database-add-button-text">{t('administration.ontologies.addOntology')}</p>
                    </div>
                  </Button>
                  <Button
                    className="database-add-button"
                    onClick={() => setIsSelectOntologiesActive(true)}
                    disabled={checkPermissionList(PermissionEnum.OntologiesDelete)}
                  >
                    <div className="database-add-button-inner-container">
                      <p className="database-add-button-text">{t('administration.ontologies.selectOntologies')}</p>
                    </div>
                  </Button>
                </>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '1rem',
                  }}
                >
                  <button
                    className="modal-button delete"
                    onClick={() => {
                      deleteOntologiesFromEvent(selectedRowKeys);
                      setSelectedRowKeys([]);
                      setIsSelectOntologiesActive(false);
                    }}
                    type="button"
                    disabled={selectedRowKeys.length === 0}
                  >
                    {t('administration.ontologies.deleteAllRows', {
                      rowLength: selectedRowKeys.length,
                    })}
                  </button>
                  <button
                    className="modal-button cancel"
                    onClick={() => {
                      setSelectedRowKeys([]);
                      setIsSelectOntologiesActive(false);
                    }}
                    type="button"
                  >
                    {t('general.cancel')}
                  </button>
                  <span style={{ display: 'flex', alignItems: 'center' }}> | </span>
                  <Button className="database-add-button" onClick={handleSelectCurrentPage}>
                    <div className="database-add-button-inner-container">
                      <p className="database-add-button-text">{t('administration.ontologies.selectAllPage')}</p>
                    </div>
                  </Button>
                </div>
              )}
              {!isSelectOntologiesActive && (
                <Search
                  placeholder={t('general.search') || ''}
                  allowClear
                  onSearch={onSearch}
                  onChange={onSearch}
                  className="database-search-box"
                  value={searchValue}
                />
              )}
            </div>
            <Form form={form} component={false}>
              <CustomTable
                dataSource={tableData}
                columns={mergedColumns}
                onChange={handleTableChange}
                rowClassName="editable-row"
                onPageChange={onPageChange}
              />
            </Form>
          </div>
        </div>
      )}

      {addOntologyModal && (
        <OntologyForm
          setEditModal={modalInEditMode}
          ontologiesForm={ontologyDataInForm}
          onClickOpen
          setOnClickOpen={setAddOntologyModal}
        />
      )}
    </>
  );
};

export default OntologyTab;
